// src/Components/Markets/MarketTabs.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMarketData } from '../../store/Slices/marketSlice';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Skeleton,
  Stack,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// ---------- Helpers ----------
const nfPrice = new Intl.NumberFormat(undefined, { maximumFractionDigits: 8 });
const nfUSD = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const nfPct = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatPrice(value) {
  if (value == null || isNaN(Number(value))) return '--';
  return nfPrice.format(Number(value));
}
function formatVolume(value) {
  if (value == null || isNaN(Number(value))) return '--';
  // Show as compact USD (e.g., $1.2M)
  try {
    return Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(Number(value));
  } catch {
    return nfUSD.format(Number(value));
  }
}
function formatPct(value) {
  if (value == null || isNaN(Number(value))) return '--';
  const n = Number(value);
  const sign = n > 0 ? '+' : '';
  return `${sign}${nfPct.format(n)}%`;
}

const EmptyState = ({ onRetry }) => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="body1" sx={{ opacity: 0.8, mb: 2 }}>
      No data available
    </Typography>
    {onRetry && (
      <Button variant="outlined" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Box>
);

const TableSkeleton = ({ rows = 8, showVolume = true }) => (
  <Table size="small" stickyHeader>
    <TableHead>
      <TableRow>
        <TableCell>Coin</TableCell>
        <TableCell align="right">Price (USDT)</TableCell>
        <TableCell align="right">Change (24h)</TableCell>
        {showVolume && <TableCell align="right">24h Volume</TableCell>}
      </TableRow>
    </TableHead>
    <TableBody>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={`sk-${i}`}>
          <TableCell>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={90} />
            </Stack>
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" width={80} sx={{ ml: 'auto' }} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" width={60} sx={{ ml: 'auto' }} />
          </TableCell>
          {showVolume && (
            <TableCell align="right">
              <Skeleton variant="text" width={100} sx={{ ml: 'auto' }} />
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// ---------- MarketTable ----------
const MarketTable = ({ coins, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showVolume = !isMobile;

  const rows = useMemo(() => {
    if (!Array.isArray(coins)) return [];
    return coins.slice(0, 50);
  }, [coins]);

  if (loading) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          bgcolor: theme.palette.background.paper,
          maxHeight: 440,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 0 },
          borderRadius: 2,
        }}
      >
        <TableSkeleton rows={10} showVolume={showVolume} />
      </TableContainer>
    );
  }

  if (!rows.length) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        <EmptyState />
      </TableContainer>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        bgcolor: theme.palette.background.paper,
        maxHeight: 440,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 0 },
        borderRadius: 2,
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Coin</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Price (USDT)</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Change (24h)</TableCell>
            {showVolume && <TableCell sx={{ fontWeight: 700 }} align="right">24h Volume</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((coin) => {
            const symbol = coin?.symbol || coin?.Symbol || '--';
            const name = coin?.name || coin?.Name || symbol;
            const price = coin?.price ?? coin?.Price;
            const change = coin?.change ?? coin?.change24h ?? coin?.Change;
            const volume24h = coin?.volume24h ?? coin?.Volume24h ?? coin?.volume;

            const isUp = Number(change) >= 0;
            const changeColor = isUp ? theme.palette.success.main : theme.palette.error.main;
            const brandText = theme.palette.text.primary;

            return (
              <TableRow key={`${symbol}-${name}`}>
                <TableCell>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    {/* Placeholder avatar circle to align like global platforms */}
                    {/* <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: theme.palette.action.hover,
                      }}
                    /> */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {symbol}
                      </Typography>
                      <Chip
                        label={name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: theme.palette.divider,
                          color: brandText,
                          height: 22,
                        }}
                      />
                    </Stack>
                  </Stack>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatPrice(price)}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ color: changeColor }}>
                    {isUp ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatPct(change)}
                    </Typography>
                  </Stack>
                </TableCell>

                {showVolume && (
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {formatVolume(volume24h)}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---------- MarketTabs ----------
export default function MarketTabs() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const brandColor = theme.palette?.brand?.main || theme.palette.primary.main;

  const { allHotCoins, gainers, losers, status, error } = useSelector((s) => s.market);
  const [tabIndex, setTabIndex] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loading = status === 'loading';

  const doRefresh = useCallback(() => {
    dispatch(fetchMarketData()).finally(() => {
      setLastUpdated(new Date());
    });
  }, [dispatch]);

  useEffect(() => {
    if (status === 'idle') {
      doRefresh();
    }
    const interval = setInterval(() => {
      doRefresh();
    }, 60_000);
    return () => clearInterval(interval);
  }, [status, doRefresh]);

  const tabSx = (active) => ({
    color: active ? brandColor : theme.palette.text.primary,
    borderColor: active ? brandColor : 'transparent',
    borderBottom: active ? `2px solid ${brandColor}` : 'none',
    textTransform: 'none',
    fontWeight: active ? 700 : 500,
    '&:hover': {
      color: brandColor,
      borderBottom: `2px solid ${brandColor}`,
    },
    transition: 'color 0.25s, border-color 0.25s',
    minHeight: 44,
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: brandColor, height: 2 },
              minHeight: 44,
            }}
          >
            <Tab label="Hot Coins" sx={tabSx(tabIndex === 0)} />
            <Tab label="Gainers" sx={tabSx(tabIndex === 1)} />
            <Tab label="Losers" sx={tabSx(tabIndex === 2)} />
          </Tabs>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={doRefresh} disabled={loading} size="small">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : '—'}
            </Typography>
          </Stack>
        </Stack>

        {error && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="error">
              Error: {String(error)}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          {tabIndex === 0 && <MarketTable coins={allHotCoins} loading={loading && (!allHotCoins || allHotCoins.length === 0)} />}
          {tabIndex === 1 && <MarketTable coins={gainers} loading={loading && (!gainers || gainers.length === 0)} />}
          {tabIndex === 2 && <MarketTable coins={losers} loading={loading && (!losers || losers.length === 0)} />}
        </Box>
      </Paper>
    </Box>
  );
}
